import { resolve } from "path";
import { Constants as ST } from "./session";
import { notify } from "./toast";
import { delay, exportForST, uuidv4 } from "./util";
import { decideReviewAlert } from "./visitorAlertGate";
import { findWindow, isValidWindow, windowInfo } from "./windowManager";

exportForST({
    ServerMessage: {
        RegisterMessageHandler: registerServerMessage
    }
});

const EVENTSOURCE_WINDOW_KEY = 'ST_SESSION_ServerMessage_Window';
const EVENTSOURCE_INSTANCE_KEY = 'ST_SESSION_ServerMessage_Instance';
const instanceId = uuidv4();
const registeredCallbacks: Record<string, Function> = {};
const channel = new BroadcastChannel("ServerEventMessages");
let wsEvents: EventSource | undefined;

/**
 * Register a listener for messages sent from the server.
 * @param name The class name of the message being sent from the server
 * @param callback A callback handler. Only one handler for each message is allowed
 * @returns An unregister method to remove the listener
 */
export function registerServerMessage<T>(name: string, callback: (data: T) => void) {
    registeredCallbacks[name] = callback;

    return function unregister() {
        delete registeredCallbacks[name];
    };
}

document.addEventListener('DOMContentLoaded', () => {
    if (!ST.disablePolling || (window.ST as any).DisablePolling === false) {
        // poll and take ownership if the current event source when owner is closed
        setInterval(checkSingletonOwnership, 5000);
        checkSingletonOwnership(true);

        function checkSingletonOwnership(firstCheck = false) {
            if (localStorage[EVENTSOURCE_WINDOW_KEY] === windowInfo().windowName
                && localStorage[EVENTSOURCE_INSTANCE_KEY] === windowInfo().instance) {
                if (!wsEvents) {
                    initServerEvents();
                }
                return;
            }

            wsEvents?.close();
            wsEvents = undefined;

            if (firstCheck) {
                const currentOwnerName = localStorage[EVENTSOURCE_WINDOW_KEY];
                // findWindow internally clears localStorage if the window has since died
                findWindow(currentOwnerName);
            }


            if (!isValidWindow(localStorage[EVENTSOURCE_WINDOW_KEY], localStorage[EVENTSOURCE_INSTANCE_KEY])) {
                localStorage[EVENTSOURCE_WINDOW_KEY] = windowInfo().windowName;
                localStorage[EVENTSOURCE_INSTANCE_KEY] = windowInfo().instance;
                initServerEvents();
            }
        }
    }
});

// Listen on the broadcast channel for any events being posted from the singleton wsEvents
channel.onmessage = (msg) => {
    if (msg.data?.name) {
        invokeCallback(msg.data?.name, msg.data?.data);
    }
};

function invokeCallback(name: string, data: unknown) {
    const callback = registeredCallbacks[name];
    if (callback) {
        try {
            callback(data);
        }
        catch (e) {
            console.error(e);
        }
    }
}

function initServerEvents() {
    const isMobile = document.body.classList.contains('Mobile');

    if (!ST.impersonated && !wsEvents && !isMobile) {
        let errCount = 0;
        let errTimer: NodeJS.Timeout;

        console.log(`%cConnecting To SSE EventSource (${instanceId})`, 'color: green');
        wsEvents = new EventSource('api/v1/st_sse');

        wsEvents.onmessage = (ev) => {
            if (!ev.data) return;

            try {
                const data = JSON.parse(ev.data);
                const name = data.Type;
                invokeCallback(name, data);
                channel.postMessage({ name, data });
            } catch (e) {
                console.error('Error processing server event message', e);
            }
        }

        // Add a grace period for errors
        wsEvents.onerror = function (ev) {
            console.log('%cSSE EventSource Error', 'color: red', ev);
            clearTimeout(errTimer);
            errTimer = setTimeout(() => errCount = 0, 5000);

            if (wsEvents && errCount++ > 3) {
                wsEvents.close();
                wsEvents = undefined;
            }
        };
    }
}

registerServerMessage("SSE_Initialized", (data) => {
    console.log('%cServer Side Events Initialized', 'color: green');
});

// Chrome (and other browsers) silently drop speechSynthesis.speak() calls that aren't triggered by a
// genuine user gesture — which a background SSE-triggered announcement never is. Priming the engine
// once, inside the very first real click/keydown on the page, unlocks it for the rest of the session
// (confirmed: a click-triggered speak() call works; a console- or event-triggered one without a prior
// gesture does not). A staff member logs in and clicks something within moments, so this is
// transparent in practice.
let speechSynthesisUnlocked = false;
function unlockSpeechSynthesis() {
    if (speechSynthesisUnlocked || !('speechSynthesis' in window)) {
        return;
    }

    speechSynthesisUnlocked = true;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(' '));
}
document.addEventListener('click', unlockSpeechSynthesis, { once: true });
document.addEventListener('keydown', unlockSpeechSynthesis, { once: true });

// Announces a check-in-style message (e.g. a student added to a counselor's queue, or a health-room
// check-in) to a staff member's Synergy web session, regardless of which view they currently have
// open — mirrors the same text-to-speech announcement already shipped on CounselVUE/HealthVUE.
// Gated on ST.ShowExperimental and ST.PlaySoundOnCheckIn (read from window.ST directly; the Constants
// re-export above is not populated with these flags). Both are snapshotted at page load, so toggling
// the preference on Counselor Student Check-In / Health Log takes effect on the next page load.
// PNotify's `icon` option takes a CSS icon-font class, rendered the same way as its own built-in
// success/error icons (see toast.tsx's `notify`) - not an <img>/URL, since raw-HTML icon support
// couldn't be confirmed from the (minified) vendored PNotify source.
const CHECKIN_KIND_ICON: Record<string, string> = {
    Counselor: 'fa fa-user',
};

registerServerMessage<{ Message?: string; Kind?: string }>("SSE_CheckInAnnouncement", (data) => {
    const st = (window as any).ST;
    if (!st?.ShowExperimental || !st?.PlaySoundOnCheckIn || !data?.Message) {
        return;
    }

    notify('success', data.Message, data.Kind ? CHECKIN_KIND_ICON[data.Kind] : undefined);

    if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(new SpeechSynthesisUtterance(data.Message));
    }
});

// Pushed to a user's own session(s) the instant they toggle the "play sound on check-in" preference
// (Counselor Student Check-In / Health Log action-area button), so this already-loaded page's
// cached window.ST.PlaySoundOnCheckIn flag stops being stale — without this, toggling Off had no
// effect until the next full page load because SSE_CheckInAnnouncement above only ever reads the
// page-load-time snapshot.
registerServerMessage<{ PlaySoundOnCheckIn?: boolean }>("SSE_PlaySoundPreferenceChanged", (data) => {
    const st = (window as any).ST;
    if (!st || data?.PlaySoundOnCheckIn === undefined) {
        return;
    }

    st.PlaySoundOnCheckIn = data.PlaySoundOnCheckIn;

    if (data.PlaySoundOnCheckIn === false && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
    }
});

// ── Visitor Management review chime ──────────────────────────────────────────────────────────────
//
// Deliberately NOT speech. The check-in announcement above reads its message aloud; this is a short
// two-tone chime, because its whole job is to be recognisable as "Visitor Management wants somebody"
// from across a front office. A spoken sentence is slower to register and easy to confuse with the
// counselling/health announcements that already talk.
//
// Synthesised rather than an audio file: no binary to add to the bundle, nothing to 404, and it
// sounds identical on every machine. Two descending notes (988Hz -> 659Hz, B5 -> E5) - a doorbell
// shape, distinct from the single beeps Windows and the browser make on their own.
let vmAudioCtx: AudioContext | undefined;

function playVisitorReviewChime() {
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return;

    try {
        // One context per page, reused: browsers cap how many can exist, and creating one per chime
        // leaks them on a desk that sees a lot of visitors.
        if (!vmAudioCtx) vmAudioCtx = new Ctor();
        const ctx = vmAudioCtx!;

        // The same autoplay rule the speech unlock above exists for: a context created before any
        // user gesture starts suspended, and resume() only succeeds once the page has been
        // interacted with. Staff click something within moments of logging in, so this is
        // transparent in practice.
        if (ctx.state === 'suspended') void ctx.resume();

        const now = ctx.currentTime;
        [{ freq: 987.77, at: 0 }, { freq: 659.25, at: 0.18 }].forEach(({ freq, at }) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;

            // Eased in and out - a square-edged gain change clicks audibly.
            const start = now + at;
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);

            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.4);
        });
    } catch (e) {
        // A chime must never break the page it is announcing on.
        console.error('Error playing visitor review chime:', e);
    }
}

// Fired when a check-in lands in the review queue and needs staff to match or approve the person.
// Broadcast server-side to every open Synergy session (there is no roster of "who watches the front
// desk") - it carries no station or user targeting of its own. WHAT ACTUALLY GATES IT is client-side,
// in decideReviewAlert just below: PAD access to the Visitor Check In / Check Out view
// (VmVisitorViewAccess), the message's org-year matching this session's own focus org-year, and two
// per-user toggles (VmReviewToast / VmReviewSound) that both default ON. A session that fails any of
// those shows nothing.
//
// STATION TARGETING WAS DESIGNED, HALF-BUILT, THEN ABANDONED - stood down 2026-08-11 so a demo could
// show the working toast - and is NOT part of the gate above. That device-level filter was fully
// specified and half built, but it fails CLOSED: with no station cached and no DB column to flag one,
// every browser would have gone silent. See
// docs/superpowers/plans/2026-08-11-station-alerts-RESTORE-NOTES.md before restoring it.
registerServerMessage<{ OrganizationYearGU?: string; AlertStationGUs?: string; Message?: string }>("SSE_VisitorManagementReview", (data) => {
    const st = (window as any).ST;
    if (!st?.VmSelfCheckInEnabled || !data?.Message) {
        return;
    }

    // Scope, PAD access and preference all live in decideReviewAlert - see that file for why this
    // gate fails CLOSED where every other Visitor Management filter fails open.
    const decision = decideReviewAlert({
        messageOrgYearGU: data.OrganizationYearGU,
        focusOrgYearGU: st.FocusOrgYearGU,
        hasVisitorViewAccess: st.VmVisitorViewAccess === true,
        toastEnabled: st.VmReviewToast !== false,
        soundEnabled: st.VmReviewSound !== false,
    });

    if (!decision.toast && !decision.chime) {
        return;
    }

    if (decision.toast) {
        // 'notice', NOT 'success'. Nothing has succeeded - somebody is waiting to be matched or
        // approved, and a green tick misreads that. 'error' would be equally wrong.
        notify('notice', data.Message);
    }
    if (decision.chime) {
        playVisitorReviewChime();
    }
});

// Pushed to a user's own session(s) the instant they toggle either Visitor Management review alert
// preference, so this already-loaded page's cached window.ST.VmReviewToast/VmReviewSound flags stop
// being stale - without this the toggles would appear to do nothing until a full page reload, the
// exact bug SSE_PlaySoundPreferenceChanged (above) was added to fix for the older sound preference.
registerServerMessage<{ ToastEnabled?: boolean; SoundEnabled?: boolean }>("SSE_VisitorAlertPreferenceChanged", (data) => {
    const st = (window as any).ST;
    if (!st) return;
    if (data?.ToastEnabled !== undefined) st.VmReviewToast = data.ToastEnabled;
    if (data?.SoundEnabled !== undefined) st.VmReviewSound = data.SoundEnabled;
});

// Sent ONLY to members of the school's configured block/allow notification group, one targeted
// message each - so there is no org-year or PAD filtering to do here. Membership IS the audience.
//
// NO CHIME, and NOT silenced by the review-alert toggles. These are a safety/awareness channel a
// district configured deliberately; an individual muting them would leave that district believing
// somebody is being told when they are not.
registerServerMessage<{ Message?: string, IsBlockEvent?: boolean }>("SSE_VisitorGroupNotice", (data) => {
    const st = (window as any).ST;
    if (!st?.VmSelfCheckInEnabled || !data?.Message) {
        return;
    }
    // A deny reads as an error and an allow as a success, so the colour alone carries which way the
    // decision went before anybody reads the words. This is the ONE place the two are told apart -
    // the message text is deliberately detail-free, so without the colour a glanced-at deny is
    // indistinguishable from a glanced-at allow.
    //
    // Defaults to 'success' on a missing flag rather than 'error'. An absent field means an older
    // server, not a deny, and colouring routine check-ins red would train people to ignore the red
    // one that matters.
    notify(data.IsBlockEvent === true ? 'error' : 'success', data.Message);
});